import { useState, useEffect } from 'react';
import { PageToolbar, PageShell, SectionTabs, SECTIONS } from '../components/enterprise';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RightPanel } from '../components/ui/right-panel';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';
import { 
  Search,
  Star,
  ExternalLink,
  Mail,
  Loader2,
  Truck,
  Shield,
  Phone,
  MapPin,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  Filter,
  SortAsc,
  Users,
  Clock,
  FileText,
  Building2,
  Globe,
  MessageSquare,
  XCircle,
  ArrowRight,
  AlertTriangle,
  RefreshCcw,
  ChevronRight,
  ThumbsUp,
  BarChart3,
  Package,
  Eye
} from 'lucide-react';
import { getUserErrorMessage } from '../utils/errorMessages';
import { toast } from 'sonner';
import { StarRating } from '../components/leveranciers/StarRating';
import { SupplierRatingDialog, SupplierContactDialog, SupplierCompareDialog } from '../components/leveranciers/LeverancierDialogs';

const statusLabels = {
  'draft': { label: 'Concept', color: 'bg-neutral-100 text-neutral-700', icon: FileText },
  'matching': { label: 'Matching', color: 'bg-blue-100 text-blue-700', icon: Search },
  'nieuw': { label: 'Verstuurd', color: 'bg-blue-100 text-blue-700', icon: Clock },
  'contact_gelegd': { label: 'Contact gelegd', color: 'bg-yellow-100 text-yellow-700', icon: MessageSquare },
  'voorstel_gedaan': { label: 'Voorstel gedaan', color: 'bg-purple-100 text-purple-700', icon: FileText },
  'bezig': { label: 'In uitvoering', color: 'bg-orange-100 text-orange-700', icon: RefreshCcw },
  'afgerond': { label: 'Afgerond', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'gestopt': { label: 'Gestopt', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const helpTypeLabels = {
  'implementatiehulp': 'Implementatie',
  'advies': 'Advies',
  'toolingselectie': 'Tooling'
};

const LeveranciersPage = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leveranciers, setLeveranciers] = useState([]);
  const [marketplaceSuppliers, setMarketplaceSuppliers] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedExpertise, setSelectedExpertise] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [activeTab, setActiveTab] = useState('requests');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [myRisks, setMyRisks] = useState([]);
  const [filterByMyRisks, setFilterByMyRisks] = useState(false);
  
  // Request detail dialog
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetailOpen, setRequestDetailOpen] = useState(false);
  const [requestHistory, setRequestHistory] = useState([]);
  
  // Rating dialog
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [selectedSupplierForRating, setSelectedSupplierForRating] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingHover, setRatingHover] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [existingRating, setExistingRating] = useState(null);
  
  // Filter dialog state
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  // Contact form dialog state
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactSupplier, setContactSupplier] = useState(null);
  const [contactMessage, setContactMessage] = useState('');

  // Supplier profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedSupplierProfile, setSelectedSupplierProfile] = useState(null);
  const [supplierReviews, setSupplierReviews] = useState({ ratings: [], stats: null });
  const [loadingProfile, setLoadingProfile] = useState(false);

  // Comparison mode state
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const [showCompareDialog, setShowCompareDialog] = useState(false);

  const toggleCompare = (supplier) => {
    setCompareSelection(prev => {
      const exists = prev.find(s => s.id === supplier.id);
      if (exists) return prev.filter(s => s.id !== supplier.id);
      if (prev.length >= 3) { toast.error('Maximaal 3 leveranciers vergelijken'); return prev; }
      return [...prev, supplier];
    });
  };

  const openSupplierProfile = async (supplier) => {
    setSelectedSupplierProfile(supplier);
    setProfileDialogOpen(true);
    setLoadingProfile(true);
    try {
      const res = await api.get(`/ratings/supplier/${supplier.id}`);
      setSupplierReviews(res.data);
    } catch {
      setSupplierReviews({ ratings: [], stats: { average: 0, count: 0, distribution: {} } });
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, marketplaceRes, requestsRes, risksRes] = await Promise.all([
          api.get('/partners'),
          api.get('/marketplace/suppliers').catch(() => ({ data: { suppliers: [] } })),
          api.get('/marketplace/requests').catch(() => ({ data: { requests: [] } })),
          api.get('/risks').catch(() => ({ data: [] }))
        ]);
        setLeveranciers(partnersRes.data);
        setMarketplaceSuppliers(marketplaceRes.data.suppliers || []);
        setMyRequests(requestsRes.data.requests || []);
        setMyRisks(risksRes.data || []);
      } catch (error) {
        toast.error('De gegevens konden niet worden geladen. Ververs de pagina en probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api]);

  // Get risk types from my risks for filtering
  const myRiskTypes = [...new Set(myRisks.map(r => r.type).filter(Boolean))];
  const myRiskKeywords = [...new Set(myRisks.flatMap(r => {
    const words = [];
    if (r.name) words.push(...r.name.toLowerCase().split(' '));
    if (r.type) words.push(r.type.toLowerCase());
    return words;
  }))].filter(w => w.length > 3);

  // Combine all suppliers for display
  const allSuppliers = [
    ...marketplaceSuppliers.map(s => ({
      ...s,
      name: s.company_name,
      expertise: s.services,
      is_marketplace: true
    })),
    ...leveranciers.map(l => ({ ...l, is_marketplace: false }))
  ];

  // Get unique expertise tags
  const allExpertise = [...new Set(allSuppliers.flatMap(l => l.expertise || l.services || []))];

  // Filter and sort suppliers
  const filteredSuppliers = allSuppliers.filter(supplier => {
    const name = supplier.name || supplier.company_name || '';
    const description = supplier.description || '';
    const expertise = supplier.expertise || supplier.services || [];
    
    const matchesSearch = 
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      expertise.some(e => e.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesExpertise = 
      selectedExpertise === 'all' || 
      expertise.includes(selectedExpertise);

    // Filter by my risks - check if supplier expertise matches any of my risk types
    const matchesMyRisks = !filterByMyRisks || (
      expertise.some(exp => {
        const expLower = exp.toLowerCase();
        return myRiskTypes.some(riskType => expLower.includes(riskType.toLowerCase())) ||
               myRiskKeywords.some(keyword => expLower.includes(keyword));
      })
    );

    return matchesSearch && matchesExpertise && matchesMyRisks;
  }).sort((a, b) => {
    if (sortBy === 'rating') {
      return (b.rating || 0) - (a.rating || 0);
    } else if (sortBy === 'name') {
      const nameA = a.name || a.company_name || '';
      const nameB = b.name || b.company_name || '';
      return nameA.localeCompare(nameB);
    } else if (sortBy === 'projects') {
      return (b.projects_completed || b.review_count || 0) - (a.projects_completed || a.review_count || 0);
    }
    return 0;
  });

  // Filter requests
  const filteredRequests = myRequests.filter(request => {
    if (requestStatusFilter === 'all') return true;
    if (requestStatusFilter === 'active') {
      return !['afgerond', 'gestopt'].includes(request.status);
    }
    return request.status === requestStatusFilter;
  });

  const handleContact = (supplier) => {
    const email = supplier.contact_email || supplier.email;
    if (email) {
      window.location.href = `mailto:${email}?subject=Contact via Spooker`;
    } else {
      setContactSupplier(supplier);
      setContactMessage('');
      setContactFormOpen(true);
    }
  };

  const handleSendContactForm = () => {
    if (!contactMessage.trim()) {
      toast.error('Vul een bericht in');
      return;
    }
    toast.success(`Bericht verzonden naar ${contactSupplier?.name || 'leverancier'}. U ontvangt een bevestiging per e-mail.`);
    setContactFormOpen(false);
    setContactSupplier(null);
    setContactMessage('');
  };

  const handleViewRequest = async (request) => {
    try {
      const response = await api.get(`/marketplace/requests/${request.id}`);
      setSelectedRequest(response.data.request);
      setRequestHistory(response.data.status_history || []);
      setRequestDetailOpen(true);
    } catch (error) {
      toast.error('De aanvraag kon niet worden geladen. Ververs de pagina.');
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Weet u zeker dat u deze aanvraag wilt annuleren?')) return;
    
    try {
      await api.delete(`/marketplace/requests/${requestId}`);
      toast.success('Aanvraag geannuleerd');
      setRequestDetailOpen(false);
      // Refresh requests
      const response = await api.get('/marketplace/requests');
      setMyRequests(response.data.requests || []);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De aanvraag kon niet worden geannuleerd. Probeer het later opnieuw.'));
    }
  };

  const handleReportSupplier = async (supplierId) => {
    const reason = window.prompt('Beschrijf het probleem (min. 20 karakters):');
    if (!reason || reason.length < 20) {
      toast.error('Beschrijving moet minimaal 20 karakters zijn');
      return;
    }
    
    try {
      await api.post(`/marketplace/suppliers/${supplierId}/report`, {
        reason: 'anders',
        description: reason
      });
      toast.success('Melding verstuurd naar Spooker');
    } catch (error) {
      toast.error('Het bericht kon niet worden verstuurd. Probeer het later opnieuw.');
    }
  };

  // Rating functions
  const openRatingDialog = async (supplier) => {
    const supplierId = supplier.id;
    setSelectedSupplierForRating(supplier);
    setRatingValue(0);
    setRatingHover(0);
    setReviewText('');
    setExistingRating(null);
    
    // Check if user already rated this supplier
    try {
      const response = await api.get(`/ratings/my-rating/${supplierId}`);
      if (response.data.rating) {
        setExistingRating(response.data.rating);
        setRatingValue(response.data.rating.rating);
        setReviewText(response.data.rating.review || '');
      }
    } catch (error) {
      // No existing rating, that's fine
    }
    
    setRatingDialogOpen(true);
  };

  const handleSubmitRating = async () => {
    if (ratingValue === 0) {
      toast.error('Selecteer minimaal 1 ster');
      return;
    }
    
    setSubmittingRating(true);
    try {
      await api.post('/ratings', {
        supplier_org_id: selectedSupplierForRating.id,
        rating: ratingValue,
        review: reviewText || null
      });
      toast.success(existingRating ? 'Beoordeling bijgewerkt' : 'Beoordeling toegevoegd');
      setRatingDialogOpen(false);
      
      // Refresh suppliers to show updated rating
      const marketplaceRes = await api.get('/marketplace/suppliers').catch(() => ({ data: { suppliers: [] } }));
      setMarketplaceSuppliers(marketplaceRes.data.suppliers || []);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De beoordeling kon niet worden opgeslagen. Probeer het later opnieuw.'));
    } finally {
      setSubmittingRating(false);
    }
  };

  if (loading) {
    return <PageShell section="verbeteren" testId="leveranciers-page" loading />;
  }

  const activeRequestsCount = myRequests.filter(r => !['afgerond', 'gestopt'].includes(r.status)).length;

  return (
    <PageShell section="verbeteren" testId="leveranciers-page">
        {/* Page Toolbar */}
        <PageToolbar
          title="Leveranciers"
          subtitle="Zoek, vergelijk en beheer security leveranciers."
          count={leveranciers.length + marketplaceSuppliers.length}
          testId="leveranciers"
        />

        <div className="px-6 py-4 space-y-6">

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="inline-flex justify-start bg-neutral-100 p-1 rounded-lg h-auto">
            <TabsTrigger value="requests" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm relative flex items-center gap-1.5" data-testid="tab-requests">
              <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
              Mijn Aanvragen
              {activeRequestsCount > 0 && (
                <Badge className="ml-2 h-5 min-w-5 p-0 flex items-center justify-center bg-blue-600 text-white text-xs">
                  {activeRequestsCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="marketplace" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="tab-marketplace">
              <Truck className="h-3.5 w-3.5" strokeWidth={1.5} />
              Leveranciers
            </TabsTrigger>
          </TabsList>
          <TabsContent value="marketplace" className="mt-6 space-y-6">

            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                <Input
                  type="text"
                  placeholder="Zoek op naam of expertise..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="leverancier-search-input"
                />
              </div>
              <Popover open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-neutral-200"
                    data-testid="open-filter-dialog"
                  >
                    <Filter className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Filters
                    {(selectedExpertise !== 'all' || filterByMyRisks || sortBy !== 'rating') && (
                      <Badge className="ml-2 h-5 min-w-5 p-0 flex items-center justify-center bg-black text-white text-xs">
                        {[selectedExpertise !== 'all', filterByMyRisks, sortBy !== 'rating'].filter(Boolean).length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-4" align="start">
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Sorteren</label>
                      <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="h-8 text-xs" data-testid="filter-dialog-sort">
                          <SelectValue placeholder="Sorteren" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rating">Hoogste beoordeling</SelectItem>
                          <SelectItem value="projects">Meeste ervaring</SelectItem>
                          <SelectItem value="name">Naam (A-Z)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold uppercase tracking-wider text-neutral-400">Expertise</label>
                      <Select value={selectedExpertise} onValueChange={setSelectedExpertise}>
                        <SelectTrigger className="h-8 text-xs" data-testid="filter-dialog-expertise">
                          <SelectValue placeholder="Alle expertises" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Alle expertises</SelectItem>
                          {allExpertise.map((expertise) => (
                            <SelectItem key={expertise} value={expertise}>{expertise}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {myRisks.length > 0 && (
                      <Button
                        variant={filterByMyRisks ? 'default' : 'outline'}
                        size="sm"
                        className={`w-full justify-start text-xs ${filterByMyRisks ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                        onClick={() => setFilterByMyRisks(!filterByMyRisks)}
                        data-testid="filter-dialog-risks-btn"
                      >
                        <Shield className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} />
                        {filterByMyRisks ? 'Mijn risico\'s ✓' : 'Filter op mijn risico\'s'}
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="w-full text-xs text-neutral-500" onClick={() => { setSortBy('rating'); setSelectedExpertise('all'); setFilterByMyRisks(false); }}>
                      Filters wissen
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
              <Button
                variant={compareMode ? "default" : "outline"}
                onClick={() => { setCompareMode(!compareMode); if (compareMode) { setCompareSelection([]); setShowCompareDialog(false); } }}
                className={compareMode ? "bg-black text-white hover:bg-neutral-800" : "border-neutral-200"}
                data-testid="compare-toggle-button"
              >
                <BarChart3 className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Vergelijken
                {compareSelection.length > 0 && (
                  <Badge className="ml-2 h-5 min-w-5 p-0 flex items-center justify-center bg-blue-600 text-white text-xs">
                    {compareSelection.length}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Active filters display */}
            {(selectedExpertise !== 'all' || filterByMyRisks) && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-neutral-500">Actieve filters:</span>
                {selectedExpertise !== 'all' && (
                  <Badge 
                    className="bg-neutral-100 text-neutral-700 cursor-pointer hover:bg-neutral-200"
                    onClick={() => setSelectedExpertise('all')}
                  >
                    {selectedExpertise} ×
                  </Badge>
                )}
                {filterByMyRisks && (
                  <Badge 
                    className="bg-purple-100 text-purple-700 cursor-pointer hover:bg-purple-200"
                    onClick={() => setFilterByMyRisks(false)}
                  >
                    Mijn risico's ×
                  </Badge>
                )}
              </div>
            )}

            {/* Suppliers Grid */}
            {filteredSuppliers.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuppliers.map((supplier, index) => (
                  <Card 
                    key={supplier.id || index} 
                    className={`border-neutral-200 hover:shadow-md transition-shadow cursor-pointer ${compareMode && compareSelection.find(s => s.id === supplier.id) ? 'ring-2 ring-black border-black' : ''}`}
                    onClick={() => { if (!compareMode) openSupplierProfile(supplier); }}
                    data-testid={`leverancier-card-${supplier.id || index}`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          {compareMode && (
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleCompare(supplier); }}
                              className={`h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 transition-all ${
                                compareSelection.find(s => s.id === supplier.id)
                                  ? 'bg-black border-black'
                                  : 'border-neutral-300 hover:border-neutral-500'
                              }`}
                              data-testid={`compare-check-${supplier.id || index}`}
                            >
                              {compareSelection.find(s => s.id === supplier.id) && (
                                <CheckCircle className="h-3 w-3 text-white" strokeWidth={2} />
                              )}
                            </button>
                          )}
                          <div className="h-12 w-12 rounded-lg bg-neutral-100 flex items-center justify-center">
                            {supplier.is_marketplace ? (
                              <Building2 className="h-6 w-6 text-blue-600" strokeWidth={1.5} />
                            ) : (
                              <Truck className="h-6 w-6 text-neutral-600" strokeWidth={1.5} />
                            )}
                          </div>
                          <div>
                            <CardTitle className="text-base font-semibold text-black">
                              {supplier.name || supplier.company_name}
                            </CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              {supplier.is_marketplace && (
                                <Badge className="border-transparent bg-blue-50 text-blue-700 text-xs">
                                  Marketplace
                                </Badge>
                              )}
                              {(supplier.is_verified || supplier.status === 'approved') && (
                                <Badge className="border-transparent bg-green-50 text-green-700 text-xs">
                                  <CheckCircle className="h-3 w-3 mr-1" strokeWidth={1.5} />
                                  Geverifieerd
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        {supplier.rating > 0 && (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" strokeWidth={1.5} />
                            <span className="font-medium">{supplier.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-neutral-600 line-clamp-2">{supplier.description}</p>
                      
                      {/* Expertise/Services Tags */}
                      <div className="flex flex-wrap gap-1.5">
                        {(supplier.expertise || supplier.services || []).slice(0, 4).map((tag) => (
                          <Badge 
                            key={tag} 
                            variant="outline" 
                            className="text-xs"
                          >
                            {tag}
                          </Badge>
                        ))}
                        {(supplier.expertise || supplier.services || []).length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{(supplier.expertise || supplier.services).length - 4}
                          </Badge>
                        )}
                      </div>

                      {/* Actions — removed for split-view pattern */}
                      <div className="flex items-center justify-between pt-2 text-xs text-neutral-400">
                        <span>Klik voor details</span>
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <Truck className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-neutral-500">Geen leveranciers gevonden.</p>
                <Button
                  variant="link"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedExpertise('all');
                  }}
                  className="mt-2"
                >
                  Filters wissen
                </Button>
              </div>
            )}

            {/* Floating Compare Bar */}
            {compareMode && compareSelection.length >= 2 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-black text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4" data-testid="compare-floating-bar">
                <div className="flex items-center gap-2">
                  {compareSelection.map(s => (
                    <Badge key={s.id} className="bg-white/20 text-white text-xs">
                      {s.name || s.company_name}
                    </Badge>
                  ))}
                </div>
                <Button
                  size="sm"
                  className="bg-white text-black hover:bg-neutral-200"
                  onClick={() => setShowCompareDialog(true)}
                  data-testid="open-compare-dialog"
                >
                  <BarChart3 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Vergelijken ({compareSelection.length})
                </Button>
              </div>
            )}
          </TabsContent>

          {/* My Requests Tab */}
          <TabsContent value="requests" className="mt-6 space-y-6">

            {/* Filter */}
            <div className="flex gap-6">
              <Select value={requestStatusFilter} onValueChange={setRequestStatusFilter}>
                <SelectTrigger className="w-48 h-9 text-xs">
                  <Filter className="h-3.5 w-3.5 mr-2" />
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle aanvragen</SelectItem>
                  <SelectItem value="active">Actief</SelectItem>
                  <SelectItem value="nieuw">Verstuurd</SelectItem>
                  <SelectItem value="contact_gelegd">Contact gelegd</SelectItem>
                  <SelectItem value="bezig">In uitvoering</SelectItem>
                  <SelectItem value="afgerond">Afgerond</SelectItem>
                  <SelectItem value="gestopt">Gestopt</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Requests Table */}
            {filteredRequests.length > 0 ? (
              <Card className="border-neutral-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="spk-table" data-testid="requests-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Samenvatting</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Leverancier</th>
                        <th>Datum</th>
                        <th className="text-center">Wijzigen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request, idx) => {
                        const statusInfo = statusLabels[request.status] || statusLabels['nieuw'];
                        const StatusIcon = statusInfo.icon;
                        return (
                          <tr
                            key={request.id}
                            className="cursor-pointer"
                            onClick={() => handleViewRequest(request)}
                            data-testid={`request-row-${request.id}`}
                          >
                            <td>
                              <span className="text-xs font-mono text-neutral-500">A-{String(idx + 1).padStart(3, '0')}</span>
                            </td>
                            <td>
                              <p className="text-sm font-medium text-black truncate max-w-xs">{request.summary}</p>
                            </td>
                            <td>
                              <Badge className={`${statusInfo.color} border-0 text-xs`}>
                                <StatusIcon className="h-3 w-3 mr-1" strokeWidth={1.5} />
                                {statusInfo.label}
                              </Badge>
                            </td>
                            <td>
                              <span className="text-xs text-neutral-600">{helpTypeLabels[request.help_type] || request.help_type}</span>
                            </td>
                            <td>
                              <span className="text-xs text-neutral-600">{request.supplier_name || '-'}</span>
                            </td>
                            <td>
                              <span className="text-xs text-neutral-500">{new Date(request.created_at).toLocaleDateString('nl-NL')}</span>
                            </td>
                            <td className="text-center">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleViewRequest(request); }}
                                className="inline-flex items-center gap-1.5 px-2 py-1 hover:bg-neutral-100 rounded text-sm text-[#4B5563] hover:text-[#111827] transition-colors"
                                data-testid={`view-request-${request.id}`}
                                aria-label="Bekijk aanvraag"
                              >
                                <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
                                <span className="text-xs">Bekijken</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <div className="py-12 text-center">
                <FileText className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-neutral-500 mb-2">Geen aanvragen gevonden</p>
                <p className="text-sm text-neutral-400 mb-4">
                  Start een aanvraag via de "Hulp" knop bij een maatregel op de Maatregelen pagina.
                </p>
                <Button variant="outline" onClick={() => setActiveTab('marketplace')}>
                  Bekijk leveranciers
                </Button>
              </div>
            )}

            {/* Info Card */}
            <Card className="border-neutral-200 bg-neutral-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  <div className="h-10 w-10 rounded-lg bg-white border border-neutral-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-neutral-600" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-black mb-2">Hoe werkt het?</h3>
                    <p className="text-sm text-neutral-600">
                      Wanneer u hulp inschakelt bij een maatregel, wordt uw aanvraag naar de gekozen leverancier gestuurd.
                      De leverancier neemt contact met u op en houdt de status bij. U kunt hier de voortgang volgen.
                      Spooker verandert nooit automatisch uw maturity scores - u blijft altijd in controle.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Uitbreidingen Tab */}
        </Tabs>
        <RightPanel
          open={requestDetailOpen}
          onClose={() => setRequestDetailOpen(false)}
          title="Aanvraag details"
          subtitle={selectedRequest ? `Aangemaakt op ${new Date(selectedRequest.created_at).toLocaleDateString('nl-NL')}` : ''}
          testId="request-detail-panel"
          actions={selectedRequest ? (
            <>
              {selectedRequest.supplier_org_id && !['afgerond', 'gestopt'].includes(selectedRequest.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50"
                  onClick={() => handleReportSupplier(selectedRequest.supplier_org_id)}
                  data-testid="request-report-btn"
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                  Probleem
                </Button>
              )}
              {['draft', 'matching', 'nieuw'].includes(selectedRequest.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => handleCancelRequest(selectedRequest.id)}
                  data-testid="request-cancel-btn"
                >
                  <XCircle className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                  Annuleren
                </Button>
              )}
            </>
          ) : null}
        >
          {selectedRequest && (
            <div className="space-y-6">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Status</span>
                <Badge className={`${statusLabels[selectedRequest.status]?.color || ''} border-0 text-xs`}>
                  {statusLabels[selectedRequest.status]?.label || selectedRequest.status}
                </Badge>
              </div>

              {/* Supplier Info */}
              {selectedRequest.supplier_name && (
                <div className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-100">
                  <Building2 className="h-4 w-4 text-neutral-400" strokeWidth={1.5} />
                  <div>
                    <p className="text-xs font-medium text-black">{selectedRequest.supplier_name}</p>
                    <p className="text-[11px] text-neutral-500">Geselecteerde leverancier</p>
                  </div>
                </div>
              )}

              {/* Request Details */}
              <div className="space-y-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Type hulp</p>
                  <p className="text-sm text-black">{helpTypeLabels[selectedRequest.help_type] || selectedRequest.help_type}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Hulpvraag</p>
                  <p className="text-sm text-black">{selectedRequest.summary}</p>
                </div>
                {selectedRequest.desired_outcome && (
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Gewenst resultaat</p>
                    <p className="text-sm text-black">{selectedRequest.desired_outcome}</p>
                  </div>
                )}
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Urgentie</p>
                  <Badge variant="outline" className="text-xs">{selectedRequest.urgency}</Badge>
                </div>
              </div>

              {/* Status History */}
              {requestHistory.length > 0 && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-3">Statusgeschiedenis</p>
                  <div className="space-y-3">
                    {requestHistory.slice(0, 10).map((item, index) => (
                      <div key={index} className="flex items-start gap-3 text-xs">
                        <div className="h-1.5 w-1.5 mt-1.5 rounded-full bg-neutral-400 shrink-0" />
                        <div>
                          <span className="text-neutral-500">{new Date(item.timestamp).toLocaleString('nl-NL')}</span>
                          <span className="mx-1 text-neutral-300">·</span>
                          <span className="text-black">{item.new_status ? statusLabels[item.new_status]?.label : item.action?.replace(/_/g, ' ')}</span>
                          {item.note && <p className="text-neutral-400 mt-0.5">{item.note}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </RightPanel>

        {/* Rating Dialog */}
        <SupplierRatingDialog
          open={ratingDialogOpen}
          onOpenChange={setRatingDialogOpen}
          existingRating={existingRating}
          selectedSupplier={selectedSupplierForRating}
          ratingValue={ratingValue}
          setRatingValue={setRatingValue}
          ratingHover={ratingHover}
          setRatingHover={setRatingHover}
          reviewText={reviewText}
          setReviewText={setReviewText}
          submittingRating={submittingRating}
          onSubmit={handleSubmitRating}
        />

        {/* Filter Dropdown (Popover naast zoekbalk) */}

        {/* Supplier Profile Right Panel */}
        <RightPanel
          open={profileDialogOpen}
          onClose={() => setProfileDialogOpen(false)}
          title={selectedSupplierProfile?.name || selectedSupplierProfile?.company_name || 'Leverancier'}
          subtitle={selectedSupplierProfile ? (
            <div className="flex items-center gap-2 mt-0.5">
              {(selectedSupplierProfile.is_verified || selectedSupplierProfile.status === 'approved') && (
                <Badge className="bg-green-50 text-green-700 text-[10px]"><CheckCircle className="h-2.5 w-2.5 mr-0.5" />Geverifieerd</Badge>
              )}
              {selectedSupplierProfile.is_marketplace && (
                <Badge className="bg-blue-50 text-blue-700 text-[10px]">Marketplace</Badge>
              )}
            </div>
          ) : null}
          testId="supplier-profile-panel"
          actions={selectedSupplierProfile ? (
            <>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => openRatingDialog(selectedSupplierProfile)} data-testid="profile-rate-btn">
                <ThumbsUp className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                Beoordelen
              </Button>
              <div className="flex-1" />
              <Button size="sm" className="bg-black text-white text-xs hover:bg-neutral-800" onClick={() => handleContact(selectedSupplierProfile)} data-testid="profile-contact-btn">
                <Mail className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                Contact
              </Button>
            </>
          ) : null}
        >
          {selectedSupplierProfile && (
            <div className="space-y-6">
              {/* About */}
              {selectedSupplierProfile.description && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Over</p>
                  <p className="text-sm text-neutral-700 leading-relaxed">{selectedSupplierProfile.description}</p>
                </div>
              )}

              {/* Contact Info */}
              <div className="space-y-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Contact</p>
                {selectedSupplierProfile.contact_email && (
                  <div className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg">
                    <Mail className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-[10px] text-neutral-400">Email</p>
                      <p className="text-xs text-black font-medium">{selectedSupplierProfile.contact_email || selectedSupplierProfile.email}</p>
                    </div>
                  </div>
                )}
                {selectedSupplierProfile.phone && (
                  <div className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg">
                    <Phone className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-[10px] text-neutral-400">Telefoon</p>
                      <p className="text-xs text-black font-medium">{selectedSupplierProfile.phone}</p>
                    </div>
                  </div>
                )}
                {selectedSupplierProfile.location && (
                  <div className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg">
                    <MapPin className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-[10px] text-neutral-400">Locatie</p>
                      <p className="text-xs text-black font-medium">{selectedSupplierProfile.location}</p>
                    </div>
                  </div>
                )}
                {selectedSupplierProfile.website && (
                  <div className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg">
                    <Globe className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-[10px] text-neutral-400">Website</p>
                      <a href={selectedSupplierProfile.website} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-medium hover:underline">{selectedSupplierProfile.website}</a>
                    </div>
                  </div>
                )}
              </div>

              {/* Expertise */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-2">Expertise</p>
                <div className="flex flex-wrap gap-1.5">
                  {(selectedSupplierProfile.expertise || selectedSupplierProfile.services || []).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              </div>

              {/* Stats */}
              {selectedSupplierProfile.is_marketplace && (
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 bg-neutral-50 rounded-lg">
                    <p className="text-lg font-bold text-black">{selectedSupplierProfile.rating?.toFixed(1) || '0.0'}</p>
                    <div className="flex justify-center mt-1">
                      <StarRating value={Math.round(selectedSupplierProfile.rating || 0)} readonly size="sm" />
                    </div>
                  </div>
                  <div className="text-center p-3 bg-neutral-50 rounded-lg">
                    <p className="text-lg font-bold text-black">{selectedSupplierProfile.review_count || 0}</p>
                    <p className="text-[10px] text-neutral-500">Beoordelingen</p>
                  </div>
                  <div className="text-center p-3 bg-neutral-50 rounded-lg">
                    <p className="text-lg font-bold text-black">{selectedSupplierProfile.projects_completed || 0}</p>
                    <p className="text-[10px] text-neutral-500">Projecten</p>
                  </div>
                </div>
              )}

              {/* Reviews Section */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400 mb-3">Beoordelingen</p>
                {loadingProfile ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-neutral-400" strokeWidth={1.5} />
                  </div>
                ) : supplierReviews.ratings?.length > 0 ? (
                  <div className="space-y-3">
                    {supplierReviews.stats && (
                      <div className="p-3 bg-neutral-50 rounded-lg mb-3">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-black">{supplierReviews.stats.average}</p>
                            <StarRating value={Math.round(supplierReviews.stats.average)} readonly size="sm" />
                            <p className="text-[10px] text-neutral-500 mt-1">{supplierReviews.stats.count} beoordelingen</p>
                          </div>
                          <div className="flex-1 space-y-1">
                            {[5, 4, 3, 2, 1].map(star => {
                              const count = supplierReviews.stats.distribution?.[star] || 0;
                              const pct = supplierReviews.stats.count > 0 ? (count / supplierReviews.stats.count) * 100 : 0;
                              return (
                                <div key={star} className="flex items-center gap-1.5">
                                  <span className="text-[10px] text-neutral-500 w-2">{star}</span>
                                  <Star className="h-2.5 w-2.5 text-yellow-500 fill-yellow-500" />
                                  <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className="text-[10px] text-neutral-500 w-3">{count}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {supplierReviews.ratings.map((review, idx) => (
                      <div key={idx} className="p-3 border border-neutral-100 rounded-lg">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded-full bg-neutral-100 flex items-center justify-center">
                              <Users className="h-3 w-3 text-neutral-500" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-black">{review.customer_name || 'Anoniem'}</p>
                              <StarRating value={review.rating} readonly size="sm" />
                            </div>
                          </div>
                          <span className="text-[10px] text-neutral-400">
                            {review.created_at ? new Date(review.created_at).toLocaleDateString('nl-NL') : ''}
                          </span>
                        </div>
                        {review.review && <p className="text-xs text-neutral-600 mt-1">{review.review}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-neutral-500 text-center py-3">Nog geen beoordelingen</p>
                )}
              </div>
            </div>
          )}
        </RightPanel>
      </div>

      {/* Contact Form Dialog */}
      <SupplierContactDialog
        open={contactFormOpen}
        onOpenChange={setContactFormOpen}
        contactSupplier={contactSupplier}
        contactMessage={contactMessage}
        setContactMessage={setContactMessage}
        onSend={handleSendContactForm}
      />

      {/* Comparison Dialog */}
      <SupplierCompareDialog
        open={showCompareDialog}
        onOpenChange={setShowCompareDialog}
        compareSelection={compareSelection}
      />
    </PageShell>
  );
};

export default LeveranciersPage;
